import './mask-field.scss'

import _ from 'lodash'
import { first } from 'rxjs/operators'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faEraser, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons'

import { EntityField, EntityFieldStatus, RecordMaskField } from '@skinsoft/types'
import { TranslationDataStore } from '@skinsoft/entity'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { EditableTranslatedStringList, EntitySelect } from '@skinsoft/ui'
import { MaskFieldText } from '../mask-field-text'
import { MaskFieldBoolean } from '../mask-field-boolean'
import { MaskFieldDate } from '../mask-field-date'
import { MaskFieldRef } from '../mask-field-ref'
import { MaskFieldListRef } from '../mask-field-list-ref'
import { MaskFieldListTranslatedString } from '../mask-field-list-translated-string'

const lastId = 1;

export enum MaskFieldUpdateEventType {
    UPDATE = "UPDATE",
    DELETE = "DELETE",
}

export interface MaskFieldUpdateEvent {
    abstractEntity: string,
    fieldPath: string,
    value: any,
    type?: MaskFieldUpdateEventType,    
}

const ENTITY_FIELDS_REFERENCES_TYPES = [
    'ThesaurusTerm',
    'Alias',
]

@Component({
    components: {
        sEditableTranslatedStringList: EditableTranslatedStringList,
        sEntitySelect: EntitySelect,
        sMaskFieldText: MaskFieldText,
        sMaskFieldBoolean: MaskFieldBoolean,
        sMaskFieldDate: MaskFieldDate,
        sMaskFieldRef: MaskFieldRef,
        sMaskFieldListRef: MaskFieldListRef,
        sMaskFieldListTranslatedString: MaskFieldListTranslatedString,
    },
    props: [
        'entityField', 
        'fieldValue', 
        'isInMetaMask', 
        'editing', 
        'getStatus', 
        'children', 
        'dataCy',
        'fieldValueSubWemi',
        'fieldValueItem',
        'fieldValueFromRoot',
    ],
    template: require('./mask-field.html')
})
export class MaskField extends ReactiveVue {

    public entityField: EntityField;
    public fieldValue: any;
    public isInMetaMask: boolean;
    public editing: boolean
    private children: RecordMaskField[] | null
    private dataCy: string
    private fieldValueSubWemi: any
    private fieldValueItem: any
    private fieldValueFromRoot: any

    public getStatus: (entityField: EntityField) => EntityFieldStatus

    private fieldLabel: string = ""

    private value: any = null

    // Icons
    private eraseIcon = faEraser
    private plusIcon = faPlusCircle;
    private trashIcon = faTrash;

    private singleComplex = [
        'RealTimeLocation', 
        'CurrentItemState', 
        'PermanentLocation',
        'TypeOfPackaging',
    ]

    private $translationDataStore: TranslationDataStore

    private fetchLabel(key: string) {
        let translationKey: string = `field.${key}`
        if (this.entityField.entityFields
            && this.entityField.entityFields.length > 0) {
            translationKey += ".complex"
        }
        this.subscribeTo(
            this.$translationDataStore.getMessage(translationKey, this.getAppLocale()).pipe(first()),
            translation => {
                this.fieldLabel = translation
                this.setStartValue()
            },
            'translation-subscription'
        )
    }

    /**
     * Sets the field value according to its corresponding entityField value
     * Adapts the shape if necesseray, according to entityField type
     */
    private setStartValue() {
        // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
        // in this case, it is possible that the right value is inside the item and not the other wemi field
        // that is why we have fieldValueSubWemi, fieldValueItem, fieldValueFromRoot
        // see RecordMaskDisplay

        switch (this.entityField.type.type) {
            case "List":
                this.value = this.fieldValue ?? 
                    this.fieldValueSubWemi ?? 
                    this.fieldValueItem ?? 
                    this.fieldValueFromRoot ??
                    []

                if(_.isArray(this.value) && this.value.length === 0) {
                    this.addEntry()
                }
                break
            default:
                this.value = this.fieldValue ?? this.fieldValueSubWemi ?? this.fieldValueItem ?? this.fieldValueFromRoot
                break
        }
    }

    private eraseValue() {
        this.$emit(
            "value-change",
            {
                abstractEntity: this.entityField.abstractEntity,
                fieldPath: this.entityField.path,
                value: null,
            } as MaskFieldUpdateEvent
        )
    }

    private updateValue(event: MaskFieldUpdateEvent, index: number, entityFieldName: string) {
        let updatedEvent: MaskFieldUpdateEvent = {...event}
        
        updatedEvent.fieldPath = updatedEvent.fieldPath.replace(
            `${this.entityField.path}`,
            `${this.entityField.path}[${index}]`
        )

        this.reEmitChangeEvent(updatedEvent)
    }

    private needToSpan(entityField: EntityField) {
        return (entityField.type.type === "List" || this.singleComplex.includes(entityField.type.type)) && !entityField.isReadOnly;
    }

    // Methods when value is a list
    private addEntry() {
        if(this.entityField.type.referenceField && this.entityField.type.entityFieldType?.type) {
            if(this.checkEntityFieldTypeIsReference(this.entityField.type.entityFieldType.type)) {
                this.value.push('')
                return 
            }
        }
        
        this.value.push({})
    }

    private removeEntry(index: number) {
        const path = `${this.entityField.path}[${index}]`

        this.$emit(
            'value-change',
            {
                abstractEntity: this.entityField.abstractEntity,
                fieldPath: path,
                value: null,
                type: MaskFieldUpdateEventType.DELETE,
            } as MaskFieldUpdateEvent
        )
    }

    private isReadonly(entityField: EntityField) {
        return entityField.isReadOnly || this.getStatus(entityField).disabled
    }

    private hasError(entityField: EntityField) {
        return this.getStatus(entityField).error
    }

    private infoMessage(entityField: EntityField): string | undefined {
        return this.getStatus(entityField).disabledMessage
    }

    private errorMessage(entityField: EntityField): string | undefined {
        return this.getStatus(entityField).errorMessage
    }

    private getChildStatus(childEntityField: EntityField): EntityFieldStatus {
        const parentStatus = this.getStatus(this.entityField)
        let childStatus = this.getStatus(childEntityField)
        if(parentStatus.disabled) {
            childStatus.disabled = true
        }
        return childStatus
    }

    private getDataCy(index?: number): string {
        let res: string = this.entityField.path
        if(this.dataCy) {
            res = this.dataCy
        }
        if(index !== undefined && index !== null) {
            res = `${res}.${index}`
        }

        return res
    }

    private getChildDataCy(childEntityField: EntityField, index?: number) {
        if(index !== undefined && index !== null && index >= 0) {
            return childEntityField.path.replace(
                this.entityField.path, 
                `${this.getDataCy()}.${index}`
            )
        }

        return childEntityField.path.replace(
            this.entityField.path, 
            this.getDataCy()
        )
    }

    private getChildFieldValue(childEntityField: EntityField, index?: number) {
        if(!this.value) {
            return null
        }

        let actualPath = this.entityField.path
        let childPath = childEntityField.path

        let pathFromHere = childPath.replace(actualPath, '')
        if(pathFromHere.charAt(0) === '.') {
            pathFromHere = pathFromHere.substring(1)
        }

        if(index !== undefined) {
            return _.get(this.value[index], pathFromHere, null)
        }
        return this.value[pathFromHere] ?? null
    }

    private getEntityFieldListEntryValue(entry: any) {
        return _.get(entry, 'ref') ?? entry
    }

    private reEmitChangeEvent(event) {
        this.$emit("value-change", event)
    }

    private get isReferenceField(): boolean {
        if(this.entityField.type.referenceField && this.entityField.type.type !== 'List') {
            return true
        }
        else if(this.entityField.type.type){
            return this.checkEntityFieldTypeIsReference(this.entityField.type.type)
        }
        
        return false
    }

    private checkEntityFieldTypeIsReference(entityFieldType: string) {
        const lowerCaseEntityFieldType = entityFieldType.toLowerCase()
        const isReference = ENTITY_FIELDS_REFERENCES_TYPES.find(
            type => {
                return lowerCaseEntityFieldType === type.toLowerCase()
            }
        )

        if(isReference) {
            return true
        }
        return false
    }

    @Watch('entityField', { immediate: true, deep: false })
    private onEntityFieldChange() {
        this.unsubscribeFrom('translation-subscription')
        this.fetchLabel(this.entityField.path)
    }

    @Watch('$i18n.locale', { immediate: true })
    private onLocaleChange() {
        this.unsubscribeFrom('translation-subscription')
        this.fetchLabel(this.entityField.path)
    }

    @Watch('fieldValue')
    private onFieldValueChange() {
        this.setStartValue()
    }

}